import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useFetch } from '@reveni-io/reveni-ui'

import configurationServices from '@/configuration/services/services'

import { popupColorFields, popupHeaderImageField, widgetColorFields } from './constants'
import { getFieldErrors, omitFields } from './helpers'
import {
  transformRequestEditReturnProtectionAppearance,
  transformRequestResetReturnProtectionPopup,
  transformRequestResetReturnProtectionWidget,
  transformResponseReturnProtectionAppearance,
} from './transformers'

const useReturnProtectionAppearance = ({ storeId, setToastInfo }) => {
  const { t } = useTranslation()
  const [draftValues, setDraftValues] = useState({})
  const [draftHeaderImage, setDraftHeaderImage] = useState(null)
  const [fieldErrors, setFieldErrors] = useState({})
  const [uploaderKey, setUploaderKey] = useState(0)

  const {
    data: savedAppearance,
    setData: setSavedAppearance,
    loading: gettingAppearance,
    error: appearanceError,
    fetchData: getAppearance,
  } = useFetch(configurationServices.getReturnProtectionAppearance, {
    doInitialRequest: false,
    transformResponse: transformResponseReturnProtectionAppearance,
  })

  const { fetchData: editAppearance, loading: savingAppearance } = useFetch(
    configurationServices.editReturnProtectionAppearance,
    {
      doInitialRequest: false,
      transformRequest: transformRequestEditReturnProtectionAppearance,
      transformResponse: transformResponseReturnProtectionAppearance,
    }
  )

  const { fetchData: resetWidget, loading: resettingWidget } = useFetch(
    configurationServices.editReturnProtectionAppearance,
    {
      doInitialRequest: false,
      transformRequest: transformRequestResetReturnProtectionWidget,
      transformResponse: transformResponseReturnProtectionAppearance,
    }
  )

  const { fetchData: resetPopup, loading: resettingPopup } = useFetch(
    configurationServices.editReturnProtectionAppearance,
    {
      doInitialRequest: false,
      transformRequest: transformRequestResetReturnProtectionPopup,
      transformResponse: transformResponseReturnProtectionAppearance,
    }
  )

  useEffect(() => {
    getAppearance({ storeId }, null, () => {})
  }, [getAppearance, storeId])

  const appearance = savedAppearance && {
    ...savedAppearance,
    ...draftValues,
    popup_header_image_url: draftHeaderImage?.preview ?? savedAppearance.popup_header_image_url,
  }
  const hasNewHeaderImage = Boolean(draftHeaderImage)
  const hasChanges = Object.keys(draftValues).length > 0 || hasNewHeaderImage
  const isBusy = gettingAppearance || savingAppearance || resettingWidget || resettingPopup

  const discardHeaderImage = () => {
    setDraftHeaderImage(null)
    setUploaderKey(previousKey => previousKey + 1)
  }

  const revertChanges = () => {
    setDraftValues({})
    setFieldErrors({})
    discardHeaderImage()
  }

  const changeField = (field, value) => {
    setDraftValues(previousValues => ({ ...previousValues, [field]: value }))
    setFieldErrors(previousErrors => omitFields(previousErrors, [field]))
  }

  const changeHeaderImage = file => {
    const reader = new FileReader()

    reader.onload = () => {
      setDraftHeaderImage({ file, preview: reader.result })
    }

    reader.readAsDataURL(file)
    setFieldErrors(previousErrors => omitFields(previousErrors, [popupHeaderImageField]))
  }

  const showSuccessToast = () => setToastInfo({ show: true, type: 'success', message: t('Cambios guardados') })

  const showErrorToast = error => setToastInfo({ show: true, type: 'error', message: error.errorMessage })

  const onSave = async () => {
    try {
      await editAppearance({ storeId, values: draftValues, headerImage: draftHeaderImage?.file }, setSavedAppearance)
      revertChanges()
      showSuccessToast()
    } catch (error) {
      setFieldErrors(getFieldErrors(error))
      showErrorToast(error)
    }
  }

  const onResetWidget = async () => {
    try {
      await resetWidget({ storeId }, setSavedAppearance)
      setDraftValues(previousValues => omitFields(previousValues, widgetColorFields))
      setFieldErrors(previousErrors => omitFields(previousErrors, widgetColorFields))
      showSuccessToast()
    } catch (error) {
      showErrorToast(error)
    }
  }

  const onResetPopup = async () => {
    try {
      await resetPopup({ storeId }, setSavedAppearance)
      setDraftValues(previousValues => omitFields(previousValues, popupColorFields))
      setFieldErrors(previousErrors => omitFields(previousErrors, [...popupColorFields, popupHeaderImageField]))
      discardHeaderImage()
      showSuccessToast()
    } catch (error) {
      showErrorToast(error)
    }
  }

  return {
    appearance,
    appearanceError,
    fieldErrors,
    isBusy,
    savingAppearance,
    resettingWidget,
    resettingPopup,
    hasChanges,
    hasNewHeaderImage,
    uploaderKey,
    changeField,
    changeHeaderImage,
    discardHeaderImage,
    revertChanges,
    onSave,
    onResetWidget,
    onResetPopup,
  }
}

export default useReturnProtectionAppearance
